import { useCallback, useEffect, useRef, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { appPathname } from "../../../utils/contractPath";
import "./CompanySwitcher.css";

/** Única rota em que o usuário pode ativar ou trocar a empresa ativa. */
const HOME_APP_PATH = "/inicio";

const MSG_COMPANY_SWITCH_ONLY_FROM_HOME =
  "Para alterar a empresa ativa feche todas as telas e vá à tela principal do sistema.";

export function CompanySwitcher({ tabIndex, buttonRef, onButtonKeyDown }) {
  const location = useLocation();
  const { user, switchActiveCompany, activeCompanyId } = useAuth();
  const companies = user?.permitted_companies ?? [];
  const [open, setOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState({ aberto: false, texto: "" });
  const rootRef = useRef(null);
  const modalInfoOkRef = useRef(null);

  const active =
    activeCompanyId != null
      ? companies.find((c) => c.id_entity_company === activeCompanyId)
      : null;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!modalInfo.aberto) return;
    const onKey = (e) => {
      if (e.key === "Escape") setModalInfo({ aberto: false, texto: "" });
    };
    window.addEventListener("keydown", onKey);
    window.requestAnimationFrame(() => modalInfoOkRef.current?.focus());
    return () => window.removeEventListener("keydown", onKey);
  }, [modalInfo.aberto]);

  const onPick = useCallback(
    async (id) => {
      setOpen(false);
      if (id === activeCompanyId) return;
      if (appPathname(location.pathname) !== HOME_APP_PATH) {
        setModalInfo({ aberto: true, texto: MSG_COMPANY_SWITCH_ONLY_FROM_HOME });
        return;
      }
      await switchActiveCompany(id);
    },
    [activeCompanyId, location.pathname, switchActiveCompany]
  );

  const infoModal =
    modalInfo.aberto ? (
      <div
        className="usuarios-modal-overlay"
        role="presentation"
        onMouseDown={(e) => e.preventDefault()}
      >
        <div
          className="usuarios-modal usuarios-modal--tipo-info"
          role="dialog"
          aria-modal="true"
          aria-labelledby="company-switch-info-titulo"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="usuarios-modal__accent" aria-hidden />
          <div className="usuarios-modal__corpo">
            <div className="usuarios-modal__icone" aria-hidden>
              <FaInfoCircle />
            </div>
            <h3
              id="company-switch-info-titulo"
              className="usuarios-modal__titulo"
            >
              Atenção
            </h3>
            <p className="usuarios-modal__texto usuarios-modal__texto--simples">
              {modalInfo.texto}
            </p>
          </div>
          <div className="usuarios-modal__rodape">
            <button
              ref={modalInfoOkRef}
              type="button"
              className="usuarios-btn-primary usuarios-modal-ok-wide"
              onClick={() => setModalInfo({ aberto: false, texto: "" })}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    ) : null;

  if (companies.length === 0) {
    return (
      <>
        {infoModal}
        <span className="company-switcher company-switcher--empty" title="">
          Sem empresas
        </span>
      </>
    );
  }

  if (activeCompanyId == null) {
    return (
      <>
        {infoModal}
        <div className="company-switcher" ref={rootRef}>
          <button
            ref={buttonRef}
            type="button"
            className="company-switcher__cta"
            tabIndex={tabIndex}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            onKeyDown={onButtonKeyDown}
          >
            Selecionar Empresa
          </button>
          {open ? (
            <ul className="company-switcher__menu" role="listbox">
              {companies.map((c) => (
                <li key={c.id_entity_company}>
                  <button
                    type="button"
                    className="company-switcher__menu-item"
                    onClick={() => onPick(c.id_entity_company)}
                  >
                    {c.nm_entity || `Empresa #${c.id_entity_company}`}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </>
    );
  }

  const label =
    active?.nm_entity?.trim() ||
    `Empresa #${activeCompanyId}`;

  return (
    <>
      {infoModal}
      <div className="company-switcher" ref={rootRef}>
        <button
          ref={buttonRef}
          type="button"
          className="company-switcher__badge"
          tabIndex={tabIndex}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={onButtonKeyDown}
          title="Trocar empresa"
        >
          <span className="company-switcher__badge-text">{label}</span>
          <span className="company-switcher__chevron" aria-hidden>
            ▾
          </span>
        </button>
        {open ? (
          <ul className="company-switcher__menu" role="listbox">
            {companies.map((c) => (
              <li key={c.id_entity_company}>
                <button
                  type="button"
                  className={
                    c.id_entity_company === activeCompanyId
                      ? "company-switcher__menu-item company-switcher__menu-item--active"
                      : "company-switcher__menu-item"
                  }
                  onClick={() => onPick(c.id_entity_company)}
                >
                  {c.nm_entity || `Empresa #${c.id_entity_company}`}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </>
  );
}
